/**
 * Vyction Master PDP — vanilla JS with scoped init/teardown for theme editor safety.
 */
(function () {
  'use strict';

  var cleanups = new WeakMap();

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  function scheduleDeferred(fn) {
    if (window.Shopify && Shopify.designMode) {
      fn();
      return;
    }
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 1);
  }

  function registerCleanup(node, fn) {
    if (!node || typeof fn !== 'function') return;
    if (!cleanups.has(node)) cleanups.set(node, []);
    cleanups.get(node).push(fn);
  }

  function runCleanup(node) {
    if (!node || !cleanups.has(node)) return;
    cleanups.get(node).forEach(function (fn) {
      try {
        fn();
      } catch (err) {
        console.warn('[pdp-framework] cleanup error', err);
      }
    });
    cleanups.delete(node);
  }

  function mountTemplate(slide, selector, removeSelector) {
    if (!slide) return false;
    var template = qs(selector, slide);
    if (!template) return false;

    var host = qs('.pdp-gallery__media', slide) || slide;
    if (template.content) host.appendChild(template.content.cloneNode(true));
    else host.insertAdjacentHTML('beforeend', template.innerHTML);

    if (removeSelector) qsa(removeSelector, slide).forEach(function (el) { el.remove(); });
    template.remove();
    return true;
  }

  function pauseSlideMedia(slide) {
    if (!slide) return;
    qsa('video', slide).forEach(function (video) {
      video.pause();
    });
  }

  function playSlideMedia(slide) {
    if (!slide) return;

    mountTemplate(slide, '[data-pdp-embed-template]', '.pdp-gallery__embed-poster, .pdp-gallery__play-icon');
    mountTemplate(slide, '[data-pdp-video-template]', '.pdp-gallery__video-poster');

    qsa('video', slide).forEach(function (video) {
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      var playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});
    });
  }

  function initGallery(root) {
    if (!root || root.dataset.pdpGalleryInit === 'true') return;
    root.dataset.pdpGalleryInit = 'true';

    var slides = qsa('[data-pdp-slide]', root);
    var thumbs = qsa('[data-pdp-thumb]', root);
    var viewport = qs('[data-pdp-gallery-viewport]', root);
    var prevBtn = qs('[data-pdp-gallery-prev]', root);
    var nextBtn = qs('[data-pdp-gallery-next]', root);
    var counter = qs('[data-pdp-gallery-current]', root);
    if (!slides.length) return;

    var ac = new AbortController();
    var signal = ac.signal;
    var current = slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    });
    if (current < 0) current = 0;

    function scrollThumbIntoView(index) {
      if (!thumbs[index]) return;
      var behavior = window.matchMedia('(max-width: 749px)').matches ? 'auto' : 'smooth';
      thumbs[index].scrollIntoView({ behavior: behavior, block: 'nearest', inline: 'center' });
    }

    function updateThumbs(index) {
      thumbs.forEach(function (thumb, i) {
        var active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
        thumb.tabIndex = active ? 0 : -1;
      });
      if (counter) counter.textContent = String(index + 1);
      scrollThumbIntoView(index);
    }

    function setSlide(index, direction) {
      var next = (index + slides.length) % slides.length;
      if (next === current) return;

      var prevSlide = slides[current];
      var nextSlide = slides[next];

      root.classList.add('is-transitioning');
      if (direction) root.dataset.direction = direction;

      pauseSlideMedia(prevSlide);
      prevSlide.classList.remove('is-active');
      prevSlide.setAttribute('aria-hidden', 'true');

      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');
      playSlideMedia(nextSlide);
      updateThumbs(next);
      current = next;

      window.setTimeout(function () {
        root.classList.remove('is-transitioning');
        root.removeAttribute('data-direction');
      }, 360);
    }

    function step(delta) {
      setSlide(current + delta, delta > 0 ? 'next' : 'prev');
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener(
        'click',
        function () {
          setSlide(i, i > current ? 'next' : 'prev');
        },
        { signal: signal }
      );
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); }, { signal: signal });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); }, { signal: signal });

    if (viewport) {
      var startX = 0;
      var startY = 0;
      var tracking = false;

      viewport.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            step(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            step(1);
          }
        },
        { signal: signal }
      );

      viewport.addEventListener(
        'touchstart',
        function (event) {
          if (event.touches.length !== 1) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          tracking = true;
        },
        { passive: true, signal: signal }
      );

      viewport.addEventListener(
        'touchend',
        function (event) {
          if (!tracking) return;
          tracking = false;
          var touch = event.changedTouches[0];
          var dx = touch.clientX - startX;
          var dy = touch.clientY - startY;
          if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
          if (dx < 0) step(1);
          else step(-1);
        },
        { passive: true, signal: signal }
      );
    }

    playSlideMedia(slides[current]);
    updateThumbs(current);

    var visibilityObserver = null;
    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) playSlideMedia(slides[current]);
          else pauseSlideMedia(slides[current]);
        },
        { threshold: 0.2 }
      );
      visibilityObserver.observe(root);
    }

    registerCleanup(root, function () {
      ac.abort();
      if (visibilityObserver) visibilityObserver.disconnect();
      pauseSlideMedia(slides[current]);
      delete root.dataset.pdpGalleryInit;
      root.classList.remove('is-transitioning');
      root.removeAttribute('data-direction');
    });
  }

  function initGalleries(ctx, heroOnly) {
    var scope = ctx || document;
    qsa('[data-pdp-gallery]', scope).forEach(function (gallery) {
      if (heroOnly && !gallery.closest('[data-pdp-hero], .pdp-hero')) return;
      if (!heroOnly && gallery.closest('[data-pdp-hero], .pdp-hero')) return;
      initGallery(gallery);
    });
  }

  function getPurchaseLabels(purchase) {
    var root = purchase || document;
    return {
      atc: root.dataset.atcLabel || 'Add to Cart',
      soldOut:
        root.dataset.soldOutLabel ||
        (window.variantStrings && window.variantStrings.soldOut) ||
        'Sold out',
      inStock: root.dataset.inStockText || '',
      outStock: root.dataset.soldOutLabel || 'Sold out',
    };
  }

  function isSelectOptionAvailable(select) {
    var option = select.options[select.selectedIndex];
    if (!option || option.disabled) return false;
    return option.dataset.available !== 'false';
  }

  function showFormError(form, message) {
    var el = qs('[data-pdp-form-error]', form);
    if (!el) return;
    var text =
      typeof message === 'string'
        ? message
        : message && (message.description || message.message)
          ? message.description || message.message
          : 'Unable to add this item to your cart. Please try again.';
    el.hidden = false;
    el.textContent = text;
  }

  function hideFormError(form) {
    var el = qs('[data-pdp-form-error]', form);
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  function updatePurchaseState(hero, form) {
    var purchase = qs('[data-pdp-purchase]', hero) || hero;
    var labels = getPurchaseLabels(purchase);
    var select = qs('select[name="id"]', form);
    var available = true;

    if (select) {
      available = isSelectOptionAvailable(select);
    } else if (purchase.dataset.productAvailable != null) {
      available = purchase.dataset.productAvailable === 'true';
    }

    var submitBtn = qs('[type="submit"]', form);
    var stickyBtn = qs('[data-pdp-sticky-submit]');
    var availabilityEl = qs('[data-pdp-availability]', hero);

    if (submitBtn) {
      submitBtn.disabled = !available;
      if (available) submitBtn.removeAttribute('aria-disabled');
      else submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.textContent = available ? labels.atc : labels.soldOut;
      submitBtn.classList.toggle('pdp-btn--sold-out', !available);
    }

    if (stickyBtn) {
      stickyBtn.disabled = !available;
      stickyBtn.setAttribute('aria-disabled', available ? 'false' : 'true');
      stickyBtn.classList.toggle('pdp-btn--sold-out', !available);
      var sticky = qs('[data-pdp-sticky-atc]');
      if (sticky && sticky.dataset.matchHeroCta !== 'false' && submitBtn) {
        stickyBtn.textContent = submitBtn.textContent.trim();
      } else {
        stickyBtn.textContent = available ? labels.atc : labels.soldOut;
      }
    }

    if (availabilityEl && (labels.inStock || labels.outStock)) {
      availabilityEl.hidden = false;
      availabilityEl.textContent = available ? labels.inStock : labels.outStock;
      availabilityEl.classList.toggle('pdp-hero__availability--out', !available);
    }
  }

  function initProductFormAjax(hero) {
    var form = qs('#pdp-product-form', hero) || qs('form[action*="/cart/add"]', hero);
    if (!form || form.dataset.pdpAjaxInit === 'true') return;

    var cart = qs('cart-drawer') || qs('cart-notification');
    if (!cart || typeof fetchConfig !== 'function' || !window.routes) return;
    if (typeof cart.renderContents !== 'function') {
      if (!form.dataset.pdpAjaxPending) {
        form.dataset.pdpAjaxPending = 'true';
        window.setTimeout(function () {
          delete form.dataset.pdpAjaxPending;
          initProductFormAjax(hero);
        }, 0);
      }
      return;
    }

    form.dataset.pdpAjaxInit = 'true';
    var ac = new AbortController();

    form.addEventListener(
      'submit',
      function (event) {
        event.preventDefault();

        var submitBtn = qs('[type="submit"]', form);
        if (submitBtn && (submitBtn.disabled || submitBtn.getAttribute('aria-disabled') === 'true')) return;

        hideFormError(form);

        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
        }

        var config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        var formData = new FormData(form);
        if (typeof cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cart
              .getSectionsToRender()
              .map(function (section) {
                return section.id;
              })
              .join(',')
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }
        config.body = formData;

        fetch(window.routes.cart_add_url, config)
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            if (response.status) {
              showFormError(form, response.description || response.message);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'pdp-framework',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }

            hideFormError(form);

            var addedItems = [];
            if (window.PdpPurchaseOptions && typeof window.PdpPurchaseOptions.getAddedItemsPreview === 'function') {
              addedItems = window.PdpPurchaseOptions.getAddedItemsPreview();
            }
            if ((!addedItems || !addedItems.length) && window.PdpCartConfirmation) {
              addedItems = window.PdpCartConfirmation.buildAddedItemsFromResponse(response);
            }

            if (typeof cart.renderContents === 'function') {
              cart.renderContents(response);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'pdp-framework',
                  productVariantId: formData.get('id'),
                  cartData: response,
                  addedItems: addedItems,
                  addedQuantity: window.PdpCartConfirmation
                    ? window.PdpCartConfirmation.resolveAddedQuantity({ cartData: response, addedItems: addedItems }, addedItems)
                    : 1,
                });
              }
              return;
            }

            window.location = window.routes.cart_url;
          })
          .catch(function (err) {
            console.warn('[pdp-framework] add to cart failed', err);
            showFormError(form, 'Network error — check your connection and try again.');
          })
          .finally(function () {
            if (submitBtn) {
              submitBtn.classList.remove('loading');
              if (!submitBtn.disabled) submitBtn.removeAttribute('aria-disabled');
            }
          });
      },
      { signal: ac.signal }
    );

    registerCleanup(form, function () {
      ac.abort();
      delete form.dataset.pdpAjaxInit;
    });
  }

  function syncStickyFromHero(sticky, hero) {
    if (!sticky || !hero) return;

    var heroStyle = getComputedStyle(hero);
    ['--pdp-accent', '--pdp-accent-hover', '--pdp-text', '--pdp-text-muted'].forEach(function (prop) {
      var val = heroStyle.getPropertyValue(prop).trim();
      if (val) sticky.style.setProperty(prop, val);
    });

    var heroBtn = qs('[data-cro-cta="primary-atc"]', hero);
    var stickyBtn = qs('[data-cro-cta="sticky-atc"]', sticky);
    if (heroBtn && stickyBtn && sticky.dataset.matchHeroCta !== 'false') {
      stickyBtn.textContent = heroBtn.textContent.trim();
    }

    if (stickyBtn && heroBtn) {
      stickyBtn.disabled = heroBtn.disabled;
      stickyBtn.setAttribute('aria-disabled', heroBtn.disabled ? 'true' : 'false');
      stickyBtn.classList.toggle('pdp-btn--sold-out', heroBtn.disabled);
    }

    var heroPrice = qs('[data-pdp-price]', hero);
    var stickyPrice = qs('[data-pdp-sticky-price]', sticky);
    if (heroPrice && stickyPrice) stickyPrice.innerHTML = heroPrice.innerHTML;

    var heroTitle = qs('[data-pdp-hero-title]', hero);
    var stickyTitle = qs('[data-pdp-sticky-title]', sticky);
    if (heroTitle && stickyTitle) stickyTitle.textContent = heroTitle.textContent.trim();
  }

  function syncStickyHeight(sticky) {
    if (!sticky) return;
    document.documentElement.style.setProperty('--pdp-sticky-height', sticky.offsetHeight + 'px');
  }

  function initVariantPicker(hero) {
    var form = qs('form[action*="/cart/add"]', hero) || qs('form.product-form', hero);
    if (!form || form.dataset.pdpVariantInit === 'true') return;

    var select = qs('select[name="id"]', form);
    var pills = qsa('[data-variant-pill]', hero);

    if (!select) {
      form.dataset.pdpVariantInit = 'true';
      updatePurchaseState(hero, form);
      return;
    }

    form.dataset.pdpVariantInit = 'true';
    var ac = new AbortController();
    var signal = ac.signal;

    function getPricingFromOption(option) {
      if (!option) return null;
      return {
        priceFormatted: option.dataset.priceFormatted,
        compareFormatted: option.dataset.compareFormatted,
        savingsFormatted: option.dataset.savingsFormatted,
        price: parseInt(option.dataset.price, 10) || 0,
        compare: parseInt(option.dataset.compare, 10) || 0,
      };
    }

    function getPricingFromPill(pill) {
      if (!pill) return null;
      return {
        priceFormatted: pill.dataset.priceFormatted,
        compareFormatted: pill.dataset.compareFormatted,
        savingsFormatted: pill.dataset.savingsFormatted,
        price: parseInt(pill.dataset.price, 10) || 0,
        compare: parseInt(pill.dataset.compare, 10) || 0,
      };
    }

    function updateHeroPricing(pricing) {
      if (!pricing || !pricing.priceFormatted) return;

      var priceEl = qs('[data-pdp-price]', hero);
      var savingsEl = qs('[data-pdp-savings]', hero);
      if (!priceEl) return;

      var onSale = pricing.compare > pricing.price;
      if (onSale && pricing.compareFormatted) {
        priceEl.innerHTML =
          pricing.priceFormatted + ' <s data-pdp-compare>' + pricing.compareFormatted + '</s>';
      } else {
        priceEl.textContent = pricing.priceFormatted;
      }

      if (savingsEl) {
        if (onSale && pricing.savingsFormatted) {
          savingsEl.hidden = false;
          savingsEl.textContent = pricing.savingsFormatted;
        } else {
          savingsEl.hidden = true;
          savingsEl.textContent = '';
        }
      }

      var sticky = qs('[data-pdp-sticky-atc]');
      if (sticky) syncStickyFromHero(sticky, hero);
    }

    function onVariantChange() {
      if (pills.length) {
        var selectedId = select.value;
        pills.forEach(function (pill) {
          var isSelected = pill.dataset.variantId === selectedId;
          pill.classList.toggle('is-selected', isSelected);
          pill.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      }
      updateHeroPricing(getPricingFromOption(select.options[select.selectedIndex]));
      updatePurchaseState(hero, form);
    }

    pills.forEach(function (pill) {
      pill.addEventListener(
        'click',
        function () {
          if (pill.disabled) return;
          var id = pill.dataset.variantId;
          if (!id) return;

          select.value = id;
          select.dispatchEvent(new Event('change', { bubbles: true }));

          pills.forEach(function (p) {
            p.classList.remove('is-selected');
            p.setAttribute('aria-pressed', 'false');
          });
          pill.classList.add('is-selected');
          pill.setAttribute('aria-pressed', 'true');
          updateHeroPricing(getPricingFromPill(pill));
          updatePurchaseState(hero, form);
        },
        { signal: signal }
      );
    });

    select.addEventListener('change', onVariantChange, { signal: signal });
    onVariantChange();

    registerCleanup(form, function () {
      ac.abort();
      delete form.dataset.pdpVariantInit;
    });
  }

  function initQuantity(hero) {
    if (hero.dataset.pdpQtyInit === 'true') return;

    var wraps = qsa('[data-qty-wrap]', hero);
    if (!wraps.length) return;

    hero.dataset.pdpQtyInit = 'true';
    var ac = new AbortController();
    var signal = ac.signal;

    wraps.forEach(function (wrap) {
      var input = qs('[data-qty-input]', wrap);
      var minus = qs('[data-qty-minus]', wrap);
      var plus = qs('[data-qty-plus]', wrap);
      if (!input) return;

      if (minus) {
        minus.addEventListener(
          'click',
          function () {
            var v = parseInt(input.value, 10) || 1;
            input.value = String(Math.max(1, v - 1));
          },
          { signal: signal }
        );
      }

      if (plus) {
        plus.addEventListener(
          'click',
          function () {
            var v = parseInt(input.value, 10) || 1;
            input.value = String(v + 1);
          },
          { signal: signal }
        );
      }
    });

    registerCleanup(hero, function () {
      ac.abort();
      delete hero.dataset.pdpQtyInit;
    });
  }

  function initFaqDelegation() {
    if (document.documentElement.dataset.pdpFaqInit === 'true') return;
    document.documentElement.dataset.pdpFaqInit = 'true';

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-faq-question]');
      if (!btn) return;

      var section = btn.closest('[data-pdp-section]');
      var item = btn.closest('[data-faq-item]');
      if (!item) return;

      var open = item.classList.contains('is-open');
      qsa('[data-faq-item].is-open', section || document).forEach(function (el) {
        if (el !== item) {
          el.classList.remove('is-open');
          var otherBtn = qs('[data-faq-question]', el);
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
        }
      });
      item.classList.toggle('is-open', !open);
      btn.setAttribute('aria-expanded', String(!open));
    });
  }

  function initVideoFacades(ctx) {
    qsa('[data-pdp-video-facade]:not([data-pdp-video-ready])', ctx || document).forEach(function (facade) {
      facade.dataset.pdpVideoReady = 'true';
      var playBtn = qs('.pdp-video__facade-play', facade);
      if (!playBtn) return;

      var ac = new AbortController();

      playBtn.addEventListener(
        'click',
        function () {
          var type = facade.dataset.videoType;
          var src = facade.dataset.embedSrc;
          if (!src) return;

          if (type === 'file') {
            var video = document.createElement('video');
            video.src = src;
            video.controls = true;
            video.playsInline = true;
            video.preload = 'metadata';
            video.className = 'pdp-video__facade-player';
            facade.replaceWith(video);
            video.play().catch(function () {});
            return;
          }

          var iframe = document.createElement('iframe');
          iframe.src = src;
          iframe.title = playBtn.getAttribute('aria-label') || 'Product video';
          iframe.loading = 'lazy';
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.className = 'pdp-video__facade-iframe';
          facade.replaceWith(iframe);
        },
        { signal: ac.signal }
      );

      registerCleanup(facade, function () {
        ac.abort();
        delete facade.dataset.pdpVideoReady;
      });
    });
  }

  function initTestimonialModals(ctx) {
    qsa('[data-pdp-testimonial-modal]', ctx || document).forEach(function (modal) {
      if (!modal || modal.dataset.pdpTestimonialModalInit === 'true') return;

      var section = modal.closest('[data-pdp-section]') || modal.closest('.pdp-testimonials') || document;
      var cards = qsa('[data-pdp-testimonial-card]', section);
      var dialog = qs('[data-pdp-testimonial-modal-dialog]', modal);
      var content = qs('[data-pdp-testimonial-modal-content]', modal);
      var closeBtn = qs('[data-pdp-testimonial-modal-close]', modal);
      var previousFocus = null;
      var originalParent = modal.parentNode;
      var originalNextSibling = modal.nextSibling;
      var isDestroyed = false;
      var scrollLock = {
        active: false,
        y: 0,
        bodyPosition: '',
        bodyTop: '',
        bodyLeft: '',
        bodyRight: '',
        bodyWidth: '',
        bodyOverflow: '',
      };

      if (!cards.length || !dialog || !content || !closeBtn) return;

      modal.dataset.pdpTestimonialModalInit = 'true';
      var ac = new AbortController();
      var signal = ac.signal;

      if (modal.parentNode !== document.body) {
        document.body.appendChild(modal);
      }

      function getCardData(card) {
        var mediaImg = qs('.pdp-testimonial__media img', card);
        var stars = qs('.pdp-testimonial__stars', card);
        var text = qs('.pdp-testimonial__text', card);
        var name = qs('.pdp-testimonial__name', card);
        var verified = qs('.pdp-testimonial__verified', card);

        return {
          imageSrc: mediaImg ? mediaImg.currentSrc || mediaImg.src : '',
          imageAlt: mediaImg ? mediaImg.alt || '' : '',
          starsHtml: stars ? stars.innerHTML : '',
          starsLabel: stars ? stars.getAttribute('aria-label') || '' : '',
          text: text ? text.textContent.trim() : '',
          name: name ? name.textContent.trim() : '',
          verified: verified ? verified.textContent.trim() : '',
        };
      }

      function appendText(parent, tagName, className, text) {
        if (!text) return null;
        var el = document.createElement(tagName);
        el.className = className;
        el.textContent = text;
        parent.appendChild(el);
        return el;
      }

      function buildModalContent(data) {
        content.innerHTML = '';
        content.classList.toggle('pdp-testimonial-modal__content--has-media', !!data.imageSrc);

        if (data.imageSrc) {
          var media = document.createElement('div');
          media.className = 'pdp-testimonial-modal__media';
          var img = document.createElement('img');
          img.src = data.imageSrc;
          img.alt = data.imageAlt || data.name || 'Customer review photo';
          img.loading = 'eager';
          img.decoding = 'async';
          media.appendChild(img);
          content.appendChild(media);
        }

        var body = document.createElement('div');
        body.className = 'pdp-testimonial-modal__body';

        if (data.starsHtml) {
          var stars = document.createElement('div');
          stars.className = 'pdp-testimonial-modal__stars';
          if (data.starsLabel) stars.setAttribute('aria-label', data.starsLabel);
          stars.innerHTML = data.starsHtml;
          body.appendChild(stars);
        }

        appendText(body, 'blockquote', 'pdp-testimonial-modal__text', data.text);

        var meta = document.createElement('footer');
        meta.className = 'pdp-testimonial-modal__meta';
        appendText(meta, 'span', 'pdp-testimonial-modal__name', data.name);
        appendText(meta, 'span', 'pdp-testimonial-modal__verified', data.verified);
        if (meta.children.length) body.appendChild(meta);

        content.appendChild(body);
      }

      function lockPageScroll() {
        if (scrollLock.active) return;

        scrollLock.active = true;
        scrollLock.y = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        scrollLock.bodyPosition = document.body.style.position;
        scrollLock.bodyTop = document.body.style.top;
        scrollLock.bodyLeft = document.body.style.left;
        scrollLock.bodyRight = document.body.style.right;
        scrollLock.bodyWidth = document.body.style.width;
        scrollLock.bodyOverflow = document.body.style.overflow;

        document.body.classList.add('pdp-testimonial-modal-open');
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + scrollLock.y + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      }

      function unlockPageScroll() {
        var restoreY = scrollLock.y || 0;

        document.body.classList.remove('pdp-testimonial-modal-open');
        document.body.style.position = scrollLock.bodyPosition;
        document.body.style.top = scrollLock.bodyTop;
        document.body.style.left = scrollLock.bodyLeft;
        document.body.style.right = scrollLock.bodyRight;
        document.body.style.width = scrollLock.bodyWidth;
        document.body.style.overflow = scrollLock.bodyOverflow;

        if (scrollLock.active) {
          scrollLock.active = false;
          window.scrollTo(0, restoreY);
        }
      }

      function closeModal(options) {
        var restoreFocus = !(options && options.restoreFocus === false);

        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        unlockPageScroll();
        content.innerHTML = '';
        content.classList.remove('pdp-testimonial-modal__content--has-media');
        if (restoreFocus && previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function') {
          previousFocus.focus({ preventScroll: true });
        }
        previousFocus = null;
      }

      function openModal(card) {
        var data = getCardData(card);
        if (!data.text && !data.imageSrc) return;

        previousFocus = document.activeElement;
        buildModalContent(data);
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        lockPageScroll();
        closeBtn.focus({ preventScroll: true });
      }

      function syncStandardReadMore(card) {
        if (!section.classList.contains('pdp-testimonials--standard')) return;
        var text = qs('.pdp-testimonial__text', card);
        var readMore = qs('.pdp-testimonial__read-more', card);
        if (!text || !readMore) return;

        if (card.classList.contains('is-expanded')) {
          card.classList.add('is-truncatable');
          readMore.hidden = false;
          readMore.setAttribute('aria-hidden', 'false');
          readMore.textContent = 'Show less';
          readMore.setAttribute('aria-expanded', 'true');
          return;
        }

        // Temporarily ensure clamp is applied for measurement
        var truncated = text.scrollHeight > text.clientHeight + 1;
        card.classList.toggle('is-truncatable', truncated);
        readMore.hidden = !truncated;
        readMore.setAttribute('aria-hidden', truncated ? 'false' : 'true');
        readMore.textContent = 'Read more';
        readMore.setAttribute('aria-expanded', 'false');

        if (truncated) {
          readMore.hidden = false;
          card.dataset.pdpTestimonialTruncated = 'true';
        } else {
          readMore.hidden = true;
          delete card.dataset.pdpTestimonialTruncated;
        }
      }

      function syncAllStandardReadMore() {
        if (!section.classList.contains('pdp-testimonials--standard')) return;
        cards.forEach(syncStandardReadMore);
      }

      function toggleInlineExpand(card) {
        if (!card.classList.contains('is-truncatable') && !card.classList.contains('is-expanded')) return;
        var expanded = card.classList.toggle('is-expanded');
        syncStandardReadMore(card);
      }

      cards.forEach(function (card) {
        card.addEventListener(
          'click',
          function (event) {
            if (event.target.closest('a, button, input, select, textarea')) return;
            if (section.classList.contains('pdp-testimonials--standard')) {
              openModal(card);
              return;
            }
            openModal(card);
          },
          { signal: signal }
        );

        qsa('[data-pdp-testimonial-read-more]', card).forEach(function (button) {
          button.addEventListener(
            'click',
            function (event) {
              event.preventDefault();
              event.stopPropagation();
              toggleInlineExpand(card);
            },
            { signal: signal }
          );
        });

        qsa('[data-pdp-testimonial-open]', card).forEach(function (button) {
          button.addEventListener(
            'click',
            function (event) {
              event.preventDefault();
              event.stopPropagation();
              openModal(card);
            },
            { signal: signal }
          );
        });

        card.addEventListener(
          'keydown',
          function (event) {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            openModal(card);
          },
          { signal: signal }
        );
      });

      if (section.classList.contains('pdp-testimonials--standard')) {
        var scheduleReadMoreSync = function () {
          window.requestAnimationFrame(syncAllStandardReadMore);
        };
        scheduleReadMoreSync();
        window.addEventListener('resize', scheduleReadMoreSync, { signal: signal, passive: true });
        cards.forEach(function (card) {
          qsa('img', card).forEach(function (img) {
            if (img.complete) return;
            img.addEventListener('load', scheduleReadMoreSync, { signal: signal, once: true });
          });
        });
      }

      closeBtn.addEventListener('click', closeModal, { signal: signal });

      modal.addEventListener(
        'click',
        function (event) {
          if (event.target.matches('[data-pdp-testimonial-modal-backdrop]')) closeModal();
        },
        { signal: signal }
      );

      document.addEventListener(
        'keydown',
        function (event) {
          if (modal.hidden || event.key !== 'Escape') return;
          closeModal();
        },
        { signal: signal }
      );

      window.addEventListener(
        'resize',
        function () {
          if (!modal.hidden) closeModal({ restoreFocus: false });
          else unlockPageScroll();
        },
        { passive: true, signal: signal }
      );

      var breakpointQuery = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;
      if (breakpointQuery) {
        var onBreakpointChange = function () {
          if (!modal.hidden) closeModal({ restoreFocus: false });
          else unlockPageScroll();
        };
        if (typeof breakpointQuery.addEventListener === 'function') {
          breakpointQuery.addEventListener('change', onBreakpointChange, { signal: signal });
        } else if (typeof breakpointQuery.addListener === 'function') {
          breakpointQuery.addListener(onBreakpointChange);
          registerCleanup(modal, function () {
            breakpointQuery.removeListener(onBreakpointChange);
          });
        }
      }

      function cleanupModal() {
        if (isDestroyed) return;
        isDestroyed = true;
        ac.abort();
        closeModal({ restoreFocus: false });
        if (originalParent && originalParent.isConnected) {
          if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
            originalParent.insertBefore(modal, originalNextSibling);
          } else {
            originalParent.appendChild(modal);
          }
        } else if (modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
        delete modal.dataset.pdpTestimonialModalInit;
      }

      registerCleanup(modal, cleanupModal);
      if (section && section !== document) registerCleanup(section, cleanupModal);
    });
  }

  function initReviewShowMore(ctx) {
    qsa('[data-pdp-show-more-reviews]', ctx || document).forEach(function (button) {
      if (!button || button.dataset.pdpShowMoreInit === 'true') return;

      var section = button.closest('[data-pdp-section]') || button.closest('.shopify-section') || document;
      if (!section) return;

      button.dataset.pdpShowMoreInit = 'true';
      var ac = new AbortController();

      button.addEventListener(
        'click',
        function () {
          section.classList.add('pdp-reviews-expanded');
          button.setAttribute('aria-expanded', 'true');
          // Newly revealed cards may need Read more visibility sync
          window.requestAnimationFrame(function () {
            qsa('[data-pdp-testimonial-card]', section).forEach(function (card) {
              if (!section.classList.contains('pdp-testimonials--standard')) return;
              var text = qs('.pdp-testimonial__text', card);
              var readMore = qs('.pdp-testimonial__read-more', card);
              if (!text || !readMore || card.classList.contains('is-expanded')) return;
              var truncated = text.scrollHeight > text.clientHeight + 1;
              card.classList.toggle('is-truncatable', truncated);
              readMore.hidden = !truncated;
              readMore.setAttribute('aria-hidden', truncated ? 'false' : 'true');
              readMore.setAttribute('aria-expanded', 'false');
              if (truncated) {
                card.dataset.pdpTestimonialTruncated = 'true';
              } else {
                delete card.dataset.pdpTestimonialTruncated;
              }
            });
          });
        },
        { signal: ac.signal }
      );

      registerCleanup(button, function () {
        ac.abort();
        delete button.dataset.pdpShowMoreInit;
      });
    });
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function initDeferredSections(ctx) {
    initGalleries(ctx, false);
    initVideoFacades(ctx);
  }

  function initLazySections(scope) {
    var sections = qsa('[data-pdp-section]:not([data-pdp-type="hero"])', scope);
    if (!sections.length) return;

    if (!('IntersectionObserver' in window)) {
      initDeferredSections(scope);
      return;
    }

    sections.forEach(function (section) {
      if (section.dataset.pdpLazyInit === 'true') return;

      var observer = new IntersectionObserver(
        function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          section.dataset.pdpLazyInit = 'true';
          initDeferredSections(section);
          obs.disconnect();
        },
        { rootMargin: '200px 0px', threshold: 0.01 }
      );
      observer.observe(section);

      registerCleanup(section, function () {
        observer.disconnect();
        delete section.dataset.pdpLazyInit;
      });
    });
  }

  function teardownScope(scope) {
    if (!scope) return;

    qsa('[data-pdp-gallery]', scope).forEach(runCleanup);
    qsa('[data-pdp-section]', scope).forEach(runCleanup);
    qsa('[data-pdp-testimonial-modal]', scope).forEach(runCleanup);
    qsa('[data-pdp-sticky-atc]', scope).forEach(function (sticky) {
      if (window.PdpStickyAtc && typeof window.PdpStickyAtc.teardown === 'function') {
        window.PdpStickyAtc.teardown(sticky);
      }
    });
    qsa('[data-pdp-video-facade]', scope).forEach(runCleanup);

    var hero = qs('[data-pdp-hero], .pdp-hero', scope);
    if (hero) runCleanup(hero);

    var form = qs('#pdp-product-form', scope) || qs('form[action*="/cart/add"]', scope);
    if (form) runCleanup(form);

    var stickyBtn = qs('[data-pdp-sticky-submit]', scope);
    if (stickyBtn) runCleanup(stickyBtn);
  }

  function boot(ctx) {
    var scope = ctx || document;
    var hero = qs('[data-pdp-hero], .pdp-hero', scope);

    initGalleries(scope, true);
    initFaqDelegation();
    initVideoFacades(scope);
    initTestimonialModals(scope);
    initReviewShowMore(scope);

    if (hero) {
      initVariantPicker(hero);
      initQuantity(hero);
      initProductFormAjax(hero);
    }

    scheduleDeferred(function () {
      initLazySections(scope);
    });
  }

  window.PdpHeroGallery = {
    goToVariantMedia: function (hero, mediaId) {
      if (!hero || mediaId == null) return;

      var gallery = qs('[data-pdp-gallery]', hero);
      if (!gallery) return;

      var slide = qs('[data-pdp-slide][data-media-id="' + mediaId + '"]', gallery);
      if (!slide) return;

      var index = parseInt(slide.dataset.slideIndex, 10);
      if (Number.isNaN(index)) {
        index = qsa('[data-pdp-slide]', gallery).indexOf(slide);
      }
      if (index < 0) return;

      var thumb = qs('[data-pdp-thumb][data-slide-index="' + index + '"]', gallery);
      if (thumb) {
        thumb.click();
        return;
      }

      var thumbs = qsa('[data-pdp-thumb]', gallery);
      if (thumbs[index]) thumbs[index].click();
    },
  };

  onReady(function () {
    boot(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    teardownScope(event.target);
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardownScope(event.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    if (window.PdpStickyAtc && typeof window.PdpStickyAtc.init === 'function') {
      window.PdpStickyAtc.init(document);
    }
  });
})();
